import React, { useRef, useState } from 'react';
import {
  Image, Modal, Pressable, useWindowDimensions,
} from 'react-native';
import {
  Box, Center, HStack, Icon,
} from '@gluestack-ui/themed';
import {
  Check, ImageOff, Maximize2, UserRound,
} from 'lucide-react-native';
import { idPhotoUriWithCacheBust, useIdPhotoUrl } from '../useIdPhotoUrl';

export const PORTRAIT_SIZE = 150;
export const ID_DOCUMENT_WIDTH = 230;
export const ID_DOCUMENT_HEIGHT = 150;

type EmptyPhotoFrameProps = {
  width: number;
  height: number;
  icon?: typeof ImageOff;
  testID?: string;
};

function EmptyPhotoFrame({
  width, height, icon, testID,
}: EmptyPhotoFrameProps) {
  return (
    <Center w={width} h={height} borderRadius="$lg" bg="$bgSubtle" testID={testID}>
      {icon ? <Icon as={icon} size="lg" color="$textHint" /> : null}
    </Center>
  );
}

function EnlargeBadgeVisual() {
  return (
    <Box pointerEvents="none" position="absolute" top={6} right={6}>
      <Box w={26} h={26} borderRadius="$full" overflow="hidden" position="relative">
        <Box
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          bg="$bgDark"
          opacity={0.6}
        />
        <Center w={26} h={26}>
          <Icon as={Maximize2} size="2xs" color="$textInverted" />
        </Center>
      </Box>
    </Box>
  );
}

type PhotoZoomModalProps = {
  visible: boolean;
  onClose: () => void;
  testID: string;
  closeLabel: string;
  children: React.ReactNode;
};

function PhotoZoomModal({
  visible, onClose, testID, closeLabel, children,
}: PhotoZoomModalProps) {
  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable
        onPress={onClose}
        accessibilityRole="button"
        accessibilityLabel={closeLabel}
        testID={testID}
        style={{ flex: 1 }}
      >
        <Box flex={1} bg="rgba(0,0,0,0.85)" alignItems="center" justifyContent="center" p="$6">
          {children}
        </Box>
      </Pressable>
    </Modal>
  );
}

type NoPhotoLineProps = {
  bookingId: string;
  /** Space between the two frames; matches the gap between real photos. */
  gap: number;
};

/** Both photo slots at full size, empty, so the grid holds before a photo arrives. */
export function NoPhotoLine({ bookingId, gap }: NoPhotoLineProps) {
  return (
    <>
      <EmptyPhotoFrame
        width={PORTRAIT_SIZE}
        height={PORTRAIT_SIZE}
        icon={UserRound}
        testID={`id-review-selfie-thumb-${bookingId}`}
      />
      <Box w={gap} />
      <EmptyPhotoFrame
        width={ID_DOCUMENT_WIDTH}
        height={ID_DOCUMENT_HEIGHT}
        icon={ImageOff}
        testID={`id-review-document-placeholder-${bookingId}`}
      />
    </>
  );
}

type IdDocumentPhotoProps = {
  slotId: string;
  bookingId: string;
  blobPath: string | null | undefined;
  studentName: string;
  documentWidth?: number;
  documentHeight?: number;
};

export function IdDocumentPhoto({
  slotId,
  bookingId,
  blobPath,
  studentName,
  documentWidth = ID_DOCUMENT_WIDTH,
  documentHeight = ID_DOCUMENT_HEIGHT,
}: IdDocumentPhotoProps) {
  const { url, revision } = useIdPhotoUrl(slotId, bookingId, blobPath, 'document');
  const [isZoomed, setIsZoomed] = useState(false);
  const { width: screenW, height: screenH } = useWindowDimensions();
  const imageUri = url ? idPhotoUriWithCacheBust(url, revision) : null;
  const lastUriRef = useRef<string | null>(null);
  if (imageUri) {
    lastUriRef.current = imageUri;
  }
  const displayUri = imageUri ?? (isZoomed ? lastUriRef.current : null);

  const zoomSize = Math.min(screenW * 0.9, screenH * 0.9, 720);
  const openZoom = () => setIsZoomed(true);

  return (
    <Box width={documentWidth} flexShrink={0}>
      {!displayUri ? <EmptyPhotoFrame width={documentWidth} height={documentHeight} /> : null}
      {displayUri ? (
        <Pressable
          onPress={openZoom}
          accessibilityRole="button"
          accessibilityLabel={`Zoom ID card for ${studentName}`}
          testID={`id-review-photo-${bookingId}`}
        >
          <Box
            w={documentWidth}
            h={documentHeight}
            borderRadius="$lg"
            overflow="hidden"
            bg="$bgSubtle"
            position="relative"
          >
            <Image
              source={{ uri: displayUri }}
              style={{ width: documentWidth, height: documentHeight }}
              resizeMode="cover"
              accessibilityLabel={`ID card for ${studentName}`}
              testID={`id-review-photo-uri-${bookingId}`}
            />
            <EnlargeBadgeVisual />
          </Box>
        </Pressable>
      ) : null}

      <PhotoZoomModal
        visible={isZoomed}
        onClose={() => setIsZoomed(false)}
        testID={`id-review-photo-zoom-${bookingId}`}
        closeLabel="Close ID card"
      >
        {displayUri ? (
          <Image
            source={{ uri: displayUri }}
            style={{ width: zoomSize, height: zoomSize, borderRadius: 12 }}
            resizeMode="contain"
            accessibilityLabel={`ID card for ${studentName}, enlarged`}
          />
        ) : null}
      </PhotoZoomModal>
    </Box>
  );
}

type SelfiePortraitPhotoProps = {
  slotId: string;
  bookingId: string;
  blobPath: string | null | undefined;
  studentName: string;
  portraitSize?: number;
};

export function SelfiePortraitPhoto({
  slotId,
  bookingId,
  blobPath,
  studentName,
  portraitSize = PORTRAIT_SIZE,
}: SelfiePortraitPhotoProps) {
  const { url, revision } = useIdPhotoUrl(slotId, bookingId, blobPath, 'selfie');
  const [isZoomed, setIsZoomed] = useState(false);
  const { width: screenW, height: screenH } = useWindowDimensions();
  const imageUri = url ? idPhotoUriWithCacheBust(url, revision) : null;
  const lastUriRef = useRef<string | null>(null);
  if (imageUri) {
    lastUriRef.current = imageUri;
  }
  const displayUri = imageUri ?? (isZoomed ? lastUriRef.current : null);

  const zoomSize = Math.min(screenW * 0.9, screenH * 0.9, 720);
  const openZoom = () => setIsZoomed(true);

  return (
    <Box width={portraitSize} flexShrink={0}>
      {!displayUri ? <EmptyPhotoFrame width={portraitSize} height={portraitSize} /> : null}
      {displayUri ? (
        <Pressable
          onPress={openZoom}
          accessibilityRole="button"
          accessibilityLabel={`Zoom portrait for ${studentName}`}
          testID={`id-review-selfie-photo-${bookingId}`}
        >
          <Box
            w={portraitSize}
            h={portraitSize}
            borderRadius="$lg"
            overflow="hidden"
            bg="$bgSubtle"
            position="relative"
          >
            <Image
              source={{ uri: displayUri }}
              style={{ width: portraitSize, height: portraitSize }}
              resizeMode="cover"
              accessibilityLabel={`Portrait for ${studentName}`}
              testID={`id-review-selfie-uri-${bookingId}`}
            />
            <EnlargeBadgeVisual />
          </Box>
        </Pressable>
      ) : null}

      <PhotoZoomModal
        visible={isZoomed}
        onClose={() => setIsZoomed(false)}
        testID={`id-review-selfie-zoom-${bookingId}`}
        closeLabel="Close portrait"
      >
        {displayUri ? (
          <Image
            source={{ uri: displayUri }}
            style={{ width: zoomSize, height: zoomSize, borderRadius: 12 }}
            resizeMode="contain"
            accessibilityLabel={`Portrait for ${studentName}, enlarged`}
          />
        ) : null}
      </PhotoZoomModal>
    </Box>
  );
}

const CONFIRMED_THUMB_HEIGHT = 44;
const CONFIRMED_SELFIE_WIDTH = CONFIRMED_THUMB_HEIGHT;
const CONFIRMED_DOCUMENT_WIDTH = Math.round(
  (CONFIRMED_THUMB_HEIGHT * ID_DOCUMENT_WIDTH) / ID_DOCUMENT_HEIGHT,
);

/** Confirmed photos recede: grey, dimmed, still recognisable. */
const CONFIRMED_IMAGE_STYLE = { filter: 'grayscale(1)', opacity: 0.6 } as const;
const CONFIRMED_BADGE_SIZE = 20;

function ConfirmedThumb({
  uri, width, testID,
}: { uri: string | null; width: number; testID: string }) {
  return (
    <Box
      w={width}
      h={CONFIRMED_THUMB_HEIGHT}
      borderRadius="$md"
      overflow="hidden"
      bg="$bgSubtle"
      flexShrink={0}
    >
      {uri ? (
        <Image
          source={{ uri }}
          style={[{ width, height: CONFIRMED_THUMB_HEIGHT }, CONFIRMED_IMAGE_STYLE]}
          resizeMode="cover"
          testID={testID}
        />
      ) : null}
    </Box>
  );
}

type ConfirmedPhotosProps = {
  slotId: string;
  bookingId: string;
  selfieBlobPath: string | null | undefined;
  documentBlobPath: string | null | undefined;
  /** Screen-reader name of the press target; the photos themselves are the visible affordance. */
  accessibilityLabel: string;
  gap: number;
  onPress: () => void;
};

/** The photos a supervisor already confirmed: small, greyed, one check. Pressing opens them large. */
export function ConfirmedPhotos({
  slotId,
  bookingId,
  selfieBlobPath,
  documentBlobPath,
  accessibilityLabel,
  gap,
  onPress,
}: ConfirmedPhotosProps) {
  const { url: docUrl, revision: docRev } = useIdPhotoUrl(slotId, bookingId, documentBlobPath, 'document');
  const { url: selfieUrl, revision: selfieRev } = useIdPhotoUrl(slotId, bookingId, selfieBlobPath, 'selfie');
  const docUri = docUrl ? idPhotoUriWithCacheBust(docUrl, docRev) : null;
  const selfieUri = selfieUrl ? idPhotoUriWithCacheBust(selfieUrl, selfieRev) : null;

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      testID={`id-review-view-photos-${bookingId}`}
    >
      <HStack alignItems="center" position="relative" style={{ flexWrap: 'nowrap' }}>
        <ConfirmedThumb
          uri={selfieUri}
          width={CONFIRMED_SELFIE_WIDTH}
          testID={`id-review-confirmed-selfie-${bookingId}`}
        />
        <Box w={gap / 2} />
        <ConfirmedThumb
          uri={docUri}
          width={CONFIRMED_DOCUMENT_WIDTH}
          testID={`id-review-confirmed-document-${bookingId}`}
        />
        <Center
          position="absolute"
          right={-CONFIRMED_BADGE_SIZE / 3}
          bottom={-CONFIRMED_BADGE_SIZE / 3}
          w={CONFIRMED_BADGE_SIZE}
          h={CONFIRMED_BADGE_SIZE}
          borderRadius="$full"
          bg="$statusSuccess"
          borderWidth={2}
          borderColor="$bgCard"
        >
          <Icon as={Check} size="2xs" color="$textInverted" />
        </Center>
      </HStack>
    </Pressable>
  );
}

type ViewPhotosDualZoomProps = {
  slotId: string;
  bookingId: string;
  selfieBlobPath: string | null | undefined;
  documentBlobPath: string | null | undefined;
  studentName: string;
  visible: boolean;
  onClose: () => void;
};

export function ViewPhotosDualZoom({
  slotId,
  bookingId,
  selfieBlobPath,
  documentBlobPath,
  studentName,
  visible,
  onClose,
}: ViewPhotosDualZoomProps) {
  const { url: docUrl, revision: docRev } = useIdPhotoUrl(slotId, bookingId, documentBlobPath, 'document');
  const { url: selfieUrl, revision: selfieRev } = useIdPhotoUrl(slotId, bookingId, selfieBlobPath, 'selfie');
  const docUri = docUrl ? idPhotoUriWithCacheBust(docUrl, docRev) : null;
  const selfieUri = selfieUrl ? idPhotoUriWithCacheBust(selfieUrl, selfieRev) : null;

  return (
    <PhotoZoomModal
      visible={visible}
      onClose={onClose}
      testID={`id-review-view-photos-zoom-${bookingId}`}
      closeLabel="Close photos"
    >
      <HStack space="lg" alignItems="center" flexWrap="wrap" justifyContent="center">
        {selfieUri ? (
          <Image
            source={{ uri: selfieUri }}
            style={{ width: 280, height: 280, borderRadius: 12 }}
            resizeMode="contain"
            accessibilityLabel={`Portrait for ${studentName}, enlarged`}
            testID={`id-review-view-photos-zoom-selfie-uri-${bookingId}`}
          />
        ) : null}
        {docUri ? (
          <Image
            source={{ uri: docUri }}
            style={{ width: 360, height: 240, borderRadius: 12 }}
            resizeMode="contain"
            accessibilityLabel={`ID card for ${studentName}, enlarged`}
            testID={`id-review-view-photos-zoom-document-uri-${bookingId}`}
          />
        ) : null}
      </HStack>
    </PhotoZoomModal>
  );
}
